import { useMutation } from "@tanstack/react-query";
import { Eye, Mail, Send } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

interface SendRejectionEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: string;
  candidateName: string;
  jobId?: string;
  hiringManagerName?: string;
}

export function SendRejectionEmailDialog({
  open,
  onOpenChange,
  applicationId,
  candidateName,
  jobId,
  hiringManagerName,
}: SendRejectionEmailDialogProps) {
  const { toast } = useToast();
  const hmFirstName = hiringManagerName?.split(" ")[0] || "Equipe de RH";
  const candidateFirstName = candidateName.split(" ")[0];

  const [subject, setSubject] = useState("Atualização sobre seu processo seletivo");
  const [body, setBody] = useState(
    `Oi ${candidateFirstName},\n\nObrigado por participar do nosso processo seletivo! Infelizmente, dessa vez decidimos seguir com outros candidatos.\n\nDesejamos muito sucesso na sua jornada!\n\nAbraço,\n${hmFirstName}`,
  );
  const [showPreview, setShowPreview] = useState(false);

  const sendMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.applications.sendRejectionEmail({
        id: applicationId,
        feedbackSubject: subject,
        feedbackBody: body,
      });
    },
    onSuccess: () => {
      toast({ title: "Email enviado com sucesso" });
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ id: applicationId }).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      if (jobId) {
        queryClient.invalidateQueries({
          queryKey: orpc.applications.getJobKanban.queryOptions({ jobOpeningId: jobId }).queryKey,
        });
      }
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({ title: "Erro ao enviar email", description: error.message, variant: "destructive" });
    },
  });

  const canSend = subject.trim().length > 0 && body.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Enviar email de feedback — {candidateName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="rej-email-subject">Assunto do email *</Label>
            <Input
              id="rej-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Atualização sobre seu processo seletivo"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rej-email-body">Corpo do feedback *</Label>
            <Textarea
              id="rej-email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Agradecemos seu interesse e participação no processo seletivo..."
              rows={6}
            />
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
          >
            <Eye className="w-4 h-4 mr-1" />
            {showPreview ? "Ocultar prévia" : "Ver prévia do email"}
          </Button>

          {showPreview && (
            <div className="border rounded-md p-4 bg-white dark:bg-gray-900 space-y-2">
              <p className="text-xs text-muted-foreground font-medium">PRÉVIA DO EMAIL</p>
              <p className="text-sm font-medium">Assunto: {subject || "(sem assunto)"}</p>
              <hr />
              <div className="text-sm space-y-2">
                {body.split("\n").map((line, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: text lines from split have no stable identity
                  <p key={i}>{line || <br />}</p>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => sendMutation.mutate()}
            disabled={!canSend || sendMutation.isPending}
          >
            <Send className="w-4 h-4 mr-1" />
            {sendMutation.isPending ? "Enviando..." : "Enviar Email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
